'use client'

import React, { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import {
  Plus,
  Search,
  Filter,
  Download,
  Eye,
  MoreVertical,
  FileText,
  DollarSign,
  Trash2,
  Building2,
  Shield,
  User,
  Calendar,
  X,
  ChevronDown,
  ChevronUp,
  RefreshCw
} from 'lucide-react'
import EnhancedInvoiceWizard from './EnhancedInvoiceWizard'
import { useInvoices } from '@/hooks/queries'
import { useTranslation } from 'react-i18next'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface Invoice {
  id: string
  invoice_number: string
  patient: {
    first_name: string
    last_name: string
  }
  invoice_date: string
  due_date?: string
  total_amount: number
  amount_paid: number
  amount_outstanding: number
  status: string
  corporate_contract?: {
    id: string
    company_name: string
  }
  insurance_provider?: {
    id: string
    provider_name: string
  }
  appointment?: {
    doctor?: {
      id: string
      full_name: string
    }
  }
  created_by_user?: {
    full_name: string
  }
  responsible_doctor?: {
    id: string
    full_name: string
  }
  responsible_nurse?: {
    id: string
    full_name: string
  }
}

interface FilterOptions {
  companies: { id: string; company_name: string }[]
  insuranceProviders: { id: string; provider_name: string }[]
  doctors: { id: string; full_name: string }[]
}

interface InvoiceListProps {
  clinicId: string
  patientId?: string
  onViewInvoice?: (invoiceId: string) => void
}

export default function InvoiceList({
  clinicId,
  patientId,
  onViewInvoice
}: InvoiceListProps) {
  const { t } = useTranslation('financial')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [selectedPatientId, setSelectedPatientId] = useState<string>(patientId || '')
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)

  // Advanced filters
  const [companyFilter, setCompanyFilter] = useState<string>('all')
  const [insuranceFilter, setInsuranceFilter] = useState<string>('all')
  const [doctorFilter, setDoctorFilter] = useState<string>('all')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState<string>('all')

  // Note: filterOptions and totals now come from React Query below

  // Check if any advanced filter is active
  const hasActiveFilters = companyFilter !== 'all' ||
    insuranceFilter !== 'all' ||
    doctorFilter !== 'all' ||
    dateFrom !== '' ||
    dateTo !== '' ||
    invoiceTypeFilter !== 'all'

  const clearAllFilters = () => {
    setStatusFilter('all')
    setCompanyFilter('all')
    setInsuranceFilter('all')
    setDoctorFilter('all')
    setDateFrom('')
    setDateTo('')
    setInvoiceTypeFilter('all')
    setSearchTerm('')
  }

  // Build status filter for query
  const queryStatus = statusFilter !== 'all' ? statusFilter : undefined
  
  // Use React Query for caching - 30 minute cache
  const { 
    data: queryData, 
    isLoading: loading, 
    refetch,
    isFetching 
  } = useInvoices(clinicId, queryStatus, patientId)
  
  const invoices = queryData?.invoices || []
  const totals = queryData?.totals || {
    totalAmount: 0,
    totalPaid: 0,
    totalOutstanding: 0,
    countCorporate: 0,
    countInsurance: 0
  }
  
  // Legacy function for compatibility
  const fetchInvoices = () => {
    refetch()
  }
  
  // Note: All filters (search + advanced) are now applied client-side with useMemo
  const advancedFilteredInvoices = useMemo(() => {
    return invoices.filter((inv: Invoice) => {
      if (companyFilter !== 'all' && inv.corporate_contract?.id !== companyFilter) return false
      if (insuranceFilter !== 'all' && inv.insurance_provider?.id !== insuranceFilter) return false
      if (doctorFilter !== 'all' && inv.responsible_doctor?.id !== doctorFilter && inv.appointment?.doctor?.id !== doctorFilter) return false
      if (dateFrom && new Date(inv.invoice_date) < new Date(dateFrom)) return false
      if (dateTo && new Date(inv.invoice_date) > new Date(dateTo)) return false
      if (invoiceTypeFilter === 'corporate' && !inv.corporate_contract) return false
      if (invoiceTypeFilter === 'insurance' && !inv.insurance_provider) return false
      if (invoiceTypeFilter === 'private' && (inv.corporate_contract || inv.insurance_provider)) return false
      return true
    })
  }, [invoices, companyFilter, insuranceFilter, doctorFilter, dateFrom, dateTo, invoiceTypeFilter])

  // Update filter options from query data
  const filterOptions = queryData?.filterOptions || {
    companies: [],
    insuranceProviders: [],
    doctors: []
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'paid':
        return <Badge variant="default" className="bg-green-600">{t('invoices.paid')}</Badge>
      case 'partially_paid':
        return <Badge variant="default" className="bg-blue-600">{t('invoices.partiallyPaid')}</Badge>
      case 'pending':
        return <Badge variant="outline">{t('invoices.pending')}</Badge>
      case 'overdue':
        return <Badge variant="destructive">{t('invoices.overdue')}</Badge>
      case 'cancelled':
        return <Badge variant="secondary">{t('invoices.cancelled')}</Badge>
      default:
        return <Badge>{status}</Badge>
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('sv-SE', {
      style: 'currency',
      currency: 'SEK',
      minimumFractionDigits: 0
    }).format(amount)
  }

  // Apply search filter on top of advanced filters
  const filteredInvoices = useMemo(() => {
    if (!searchTerm) return advancedFilteredInvoices
    const searchLower = searchTerm.toLowerCase()
    return advancedFilteredInvoices.filter((invoice: Invoice) => (
      invoice.invoice_number?.toLowerCase().includes(searchLower) ||
      invoice.patient?.first_name?.toLowerCase().includes(searchLower) ||
      invoice.patient?.last_name?.toLowerCase().includes(searchLower)
    ))
  }, [advancedFilteredInvoices, searchTerm])

  if (loading) {
    return (
      <Card>
        <CardContent className="p-12">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Invoiced
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totals.totalAmount)}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Paid
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {formatCurrency(totals.totalPaid)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Outstanding
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {formatCurrency(totals.totalOutstanding)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Building2 className="h-4 w-4" />
              Corporate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {totals.countCorporate || 0}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              <Shield className="h-4 w-4" />
              Insurance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {totals.countInsurance || 0}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Invoices Table */}
      <Card>
        <CardHeader className="space-y-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="flex items-center gap-2">
              {t('invoices.title')}
              {hasActiveFilters && (
                <Badge variant="secondary" className="ms-2">
                  {t('invoices.filtersActive')}
                </Badge>
              )}
            </CardTitle>
            <div className="flex flex-wrap gap-2">
              {/* New Invoice Button */}
              {!patientId && (
                <Button onClick={() => setIsCreateDialogOpen(true)}>
                  <Plus className="me-2 h-4 w-4" />
                  {t('invoices.newInvoice')}
                </Button>
              )}
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('invoices.search')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 w-64"
                />
              </div>
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="rounded-md border border-input bg-background px-3 py-2"
              >
                <option value="all">{t('invoices.allStatus')}</option>
                <option value="draft">{t('invoices.draft')}</option>
                <option value="pending">{t('invoices.pending')}</option>
                <option value="partially_paid">{t('invoices.partiallyPaid')}</option>
                <option value="paid">{t('invoices.paid')}</option>
                <option value="overdue">{t('invoices.overdue')}</option>
              </select>
              {/* Advanced Filters Toggle */}
              <Button
                variant="outline"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className={hasActiveFilters ? 'border-primary text-primary' : ''}
              >
                <Filter className="me-2 h-4 w-4" />
                {t('invoices.filters')}
                {showAdvancedFilters ? (
                  <ChevronUp className="ml-2 h-4 w-4" />
                ) : (
                  <ChevronDown className="ml-2 h-4 w-4" />
                )}
              </Button>
              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearAllFilters}>
                  <X className="me-1 h-4 w-4" />
                  {t('invoices.clear')}
                </Button>
              )}
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showAdvancedFilters && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 p-4 bg-muted/50 rounded-lg border">
              {/* Invoice Type Filter */}
              <div className="space-y-2">
                <Label className="text-xs font-medium flex items-center gap-1">
                  <FileText className="h-3 w-3" />
                  {t('invoices.invoiceType')}
                </Label>
                <select
                  value={invoiceTypeFilter}
                  onChange={(e) => setInvoiceTypeFilter(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="all">{t('invoices.allTypes')}</option>
                  <option value="patient">{t('invoices.patientOnly')}</option>
                  <option value="corporate">{t('invoices.corporate')}</option>
                  <option value="insurance">{t('invoices.insurance')}</option>
                </select>
              </div>

              {/* Company Filter */}
              <div className="space-y-2">
                <Label className="text-xs font-medium flex items-center gap-1">
                  <Building2 className="h-3 w-3" />
                  {t('invoices.company')}
                </Label>
                <select
                  value={companyFilter}
                  onChange={(e) => setCompanyFilter(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="all">{t('invoices.allCompanies')}</option>
                  {filterOptions.companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.company_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Insurance Filter */}
              <div className="space-y-2">
                <Label className="text-xs font-medium flex items-center gap-1">
                  <Shield className="h-3 w-3" />
                  {t('invoices.insurance')}
                </Label>
                <select
                  value={insuranceFilter}
                  onChange={(e) => setInsuranceFilter(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="all">{t('invoices.allInsurance')}</option>
                  {filterOptions.insuranceProviders.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.provider_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Doctor Filter */}
              <div className="space-y-2">
                <Label className="text-xs font-medium flex items-center gap-1">
                  <User className="h-3 w-3" />
                  {t('invoices.doctor')}
                </Label>
                <select
                  value={doctorFilter}
                  onChange={(e) => setDoctorFilter(e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="all">{t('invoices.allDoctors')}</option>
                  {filterOptions.doctors.map((doctor) => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.full_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Date From */}
              <div className="space-y-2">
                <Label className="text-xs font-medium flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {t('invoices.from')}
                </Label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="text-sm"
                />
              </div>

              {/* Date To */}
              <div className="space-y-2">
                <Label className="text-xs font-medium flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  {t('invoices.to')}
                </Label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="text-sm"
                />
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('invoices.invoiceNo')}</TableHead>
                  <TableHead>{t('invoices.patient')}</TableHead>
                  <TableHead>{t('invoices.type')}</TableHead>
                  <TableHead>{t('invoices.companyInsurance')}</TableHead>
                  <TableHead>{t('invoices.doctor')}</TableHead>
                  <TableHead>{t('invoices.date')}</TableHead>
                  <TableHead className="text-right">{t('invoices.amount')}</TableHead>
                  <TableHead className="text-right">{t('invoices.outstanding')}</TableHead>
                  <TableHead>{t('invoices.status')}</TableHead>
                  <TableHead className="text-right">{t('invoices.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredInvoices.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8">
                      <div className="flex flex-col items-center">
                        <FileText className="h-12 w-12 text-gray-300 mb-3" />
                        <p className="text-gray-600 font-medium">{t('invoices.empty')}</p>
                        <p className="text-sm text-gray-500 mt-1">
                          {invoices.length === 0
                            ? t('invoices.emptyNone')
                            : t('invoices.emptyFilter')}
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredInvoices.map((invoice) => (
                    <TableRow key={invoice.id}>
                      <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                      <TableCell>
                        {invoice.patient?.first_name} {invoice.patient?.last_name}
                      </TableCell>
                      <TableCell>
                        {invoice.corporate_contract ? (
                          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                            <Building2 className="h-3 w-3 mr-1" />
                            {t('invoices.corporate')}
                          </Badge>
                        ) : invoice.insurance_provider ? (
                          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                            <Shield className="h-3 w-3 mr-1" />
                            {t('invoices.insurance')}
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
                            <User className="h-3 w-3 mr-1" />
                            {t('invoices.patient')}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="max-w-[150px] truncate">
                        {invoice.corporate_contract?.company_name ||
                          invoice.insurance_provider?.provider_name ||
                          '-'}
                      </TableCell>
                      <TableCell className="max-w-[120px] truncate">
                        {invoice.responsible_doctor?.full_name ||
                          invoice.appointment?.doctor?.full_name ||
                          invoice.created_by_user?.full_name ||
                          '-'}
                      </TableCell>
                      <TableCell>
                        {new Date(invoice.invoice_date).toLocaleDateString('sv-SE')}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(invoice.total_amount)}
                      </TableCell>
                      <TableCell className="text-right text-orange-600">
                        {formatCurrency(invoice.amount_outstanding)}
                      </TableCell>
                      <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Mark as Paid Button */}
                          {invoice.status !== 'paid' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50"
                              title={t('invoices.markPaid')}
                              onClick={async () => {
                                if (confirm(t('invoices.markPaidConfirm'))) {
                                  try {
                                    const response = await fetch('/api/financial/invoices', {
                                      method: 'PATCH',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({
                                        invoiceId: invoice.id,
                                        status: 'paid'
                                      })
                                    })
                                    if (response.ok) {
                                      fetchInvoices()
                                    } else {
                                      alert(t('invoices.updateFailed'))
                                    }
                                  } catch (error) {
                                    console.error('Error updating invoice:', error)
                                  }
                                }
                              }}
                            >
                              <DollarSign className="h-4 w-4" />
                            </Button>
                          )}

                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onViewInvoice?.(invoice.id)}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>

                          {/* Delete Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title={t('invoices.delete')}
                            onClick={async () => {
                              if (confirm(t('invoices.deleteConfirm'))) {
                                try {
                                  const response = await fetch(`/api/financial/invoices?invoiceId=${invoice.id}`, {
                                    method: 'DELETE'
                                  })
                                  if (response.ok) {
                                    fetchInvoices()
                                  } else {
                                    alert(t('invoices.deleteFailed'))
                                  }
                                } catch (error) {
                                  console.error('Error deleting invoice:', error)
                                }
                              }
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create Invoice Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="!w-[80vw] !max-w-[80vw] !h-[80vh] !max-h-[80vh] p-6 flex flex-col overflow-hidden">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>{t('invoices.createTitle')}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 min-h-0 overflow-hidden">
            <EnhancedInvoiceWizard
              clinicId={clinicId}
              onSuccess={(invoiceId) => {
                setIsCreateDialogOpen(false)
                fetchInvoices()
              }}
              onCancel={() => setIsCreateDialogOpen(false)}
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
